import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import type { PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { useReactToPrint } from 'react-to-print';
import type { MenuDayForDateResponse, MenuDayItemDto } from '@core/interfaces/api';
import { auth } from '@core/utils/firebase/firebase';
import { createSitesApi } from '@features/sites/api/sites.api';
import { useMenuForDatesQuery } from '../queries/menus.queries';
import { MenuPrintPage } from './menu-print-page';
import { cleanItems, withMenuLogoCacheBust } from './menu-print-templates';
import { AlaCarteMenuBuilder } from './alacarte-menu-builder';

type MenuMode = 'daily' | 'alacarte';
type MealType = 'breakfast' | 'lunch' | 'dinner';

// Canonical page order within each day
const MEAL_ORDER: MealType[] = ['breakfast', 'lunch', 'dinner'];

const MAX_DATES = 7;

function getMealItems(menuData: MenuDayForDateResponse, meal: MealType): MenuDayItemDto[] {
  const md = menuData.day?.menuData;
  if (!md) return [];
  switch (meal) {
    case 'breakfast': return md.BreakfastItems ?? [];
    case 'lunch': return md.LunchItems ?? [];
    case 'dinner': return md.DinnerItems ?? [];
  }
}

function getMealItemCount(menuData: MenuDayForDateResponse, meal: MealType): number {
  return cleanItems(getMealItems(menuData, meal)).length;
}

const MEAL_LABEL: Record<MealType, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
};

function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatChipDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// ─── Date range helpers ───────────────────────────────────────────────────────

/**
 * Returns an array of YYYY-MM-DD strings from anchorStr toward endStr
 * (inclusive), clamped to MAX_DATES entries. Always starts at the anchor
 * and steps +1 or -1 day toward the end date.
 */
function buildDateRange(anchorStr: string, endStr: string): string[] {
  const [ay, am, ad] = anchorStr.split('-').map(Number);
  const [ey, em, ed] = endStr.split('-').map(Number);
  const anchor = new Date(ay, am - 1, ad);
  const end = new Date(ey, em - 1, ed);

  const step = anchor <= end ? 1 : -1;

  const dates: string[] = [];
  const cursor = new Date(anchor);
  while (dates.length < MAX_DATES) {
    dates.push(toLocalDateString(cursor));
    if (toLocalDateString(cursor) === toLocalDateString(end)) break;
    cursor.setDate(cursor.getDate() + step);
  }
  return dates;
}

// ─── Multi-select day component ───────────────────────────────────────────────

function MultiSelectDay(
  props: PickersDayProps<Date> & {
    selectedDates?: Set<string>;
    maxReached?: boolean;
    rangeAnchor?: string | null;
    onDoubleClickDate?: (dateStr: string) => void;
    onSingleClickDate?: (dateStr: string) => void;
  },
) {
  const { selectedDates, maxReached, rangeAnchor, onDoubleClickDate, onSingleClickDate, day, ...other } = props;
  const dateStr = toLocalDateString(day);
  const isSelected = selectedDates?.has(dateStr) ?? false;
  // Disable unselected days when max is reached
  const isDisabled = other.disabled || (!isSelected && (maxReached ?? false));

  // Track last click time to distinguish single vs double clicks.
  // We intercept clicks here so that a double-click does NOT fire
  // the underlying single-click toggle twice before the range fill.
  const lastClickTimeRef = useRef<number | null>(null);
  const singleClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    if (isDisabled) return;
    const now = event.timeStamp;
    const last = lastClickTimeRef.current;

    if (last != null && now - last < 300) {
      // Double-click detected: cancel the pending single-click toggle
      if (singleClickTimerRef.current) {
        clearTimeout(singleClickTimerRef.current);
        singleClickTimerRef.current = null;
      }
      lastClickTimeRef.current = null;

      if (onDoubleClickDate) {
        onDoubleClickDate(dateStr);
      }
      return;
    }

    // First click: delay the toggle to see if a second click follows
    lastClickTimeRef.current = now;
    singleClickTimerRef.current = setTimeout(() => {
      singleClickTimerRef.current = null;
      if (onSingleClickDate) {
        onSingleClickDate(dateStr);
      }
    }, 300);
  };

  return (
    <PickersDay
      {...other}
      day={day}
      selected={isSelected}
      disabled={isDisabled}
      onClick={handleClick}
    />
  );
}

// ─── Dialog ───────────────────────────────────────────────────────────────────

export interface MenuPdfDialogProps {
  open: boolean;
  onClose: () => void;
  siteId: number;
  siteName: string;
  menuBugImageUrl?: string | null;
}

export function MenuPdfDialog({ open, onClose, siteId, siteName, menuBugImageUrl: initialBugUrl }: MenuPdfDialogProps) {
  const [menuMode, setMenuMode] = useState<MenuMode>('daily');
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  // rangeAnchor is the last single-clicked date; double-clicking another date fills the range
  const [rangeAnchor, setRangeAnchor] = useState<string | null>(null);
  const [mealTypes, setMealTypes] = useState<Set<MealType>>(new Set(['dinner']));
  const [communityName, setCommunityName] = useState(siteName.replace(/^["']+|["']+$/g, ''));
  const [showDate, setShowDate] = useState(false);
  const [bugImageUrl, setBugImageUrl] = useState(initialBugUrl ?? '');
  const [previewIndex, setPreviewIndex] = useState(0);

  const datesList = Array.from(selectedDates).sort();
  const maxReached = selectedDates.size >= MAX_DATES;

  const printRef = useRef<HTMLDivElement>(null);
  const cacheBustTimestampRef = useRef(Date.now());
  const bustedBugImageUrl = bugImageUrl
    ? withMenuLogoCacheBust(bugImageUrl, cacheBustTimestampRef.current)
    : null;
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page { size: letter; margin: 0; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    `,
    documentTitle: `${communityName}-menus`,
  });

  useEffect(() => {
    if (!open) return;
    const sitesApi = createSitesApi(auth as any);
    sitesApi.getSite(siteId).then((site) => {
      if (site.menuBugImageUrl && !initialBugUrl) setBugImageUrl(site.menuBugImageUrl);
      if (site.name) setCommunityName(site.name.replace(/^["']+|["']+$/g, ''));
    }).catch(() => {});
  }, [open, siteId, initialBugUrl]);

  useEffect(() => {
    if (!open) {
      setSelectedDates(new Set());
      setRangeAnchor(null);
      setMealTypes(new Set(['dinner']));
    }
  }, [open]);

  const menuQueries = useMenuForDatesQuery({
    siteId,
    dates: datesList,
    enabled: open && datesList.length > 0,
  });

  // Single-click: toggle the clicked date and set it as the range anchor
  // Called from MultiSelectDay's click-timeout handler (not from DateCalendar.onChange)
  const handleSingleClickDate = (dateStr: string) => {
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateStr)) {
        next.delete(dateStr);
      } else if (next.size < MAX_DATES) {
        next.add(dateStr);
      }
      return next;
    });
    setRangeAnchor(dateStr);
  };

  // Double-click on an end date: fill the range from rangeAnchor to this date (max 7 days)
  const handleDoubleClickDate = (endStr: string) => {
    if (!rangeAnchor) {
      // No anchor yet — treat as a single-click selection and set anchor
      setSelectedDates((prev) => {
        const next = new Set(prev);
        if (!next.has(endStr) && next.size < MAX_DATES) next.add(endStr);
        return next;
      });
      setRangeAnchor(endStr);
      return;
    }
    const rangeDates = buildDateRange(rangeAnchor, endStr);
    setSelectedDates((prev) => {
      const next = new Set(prev);
      // Add all dates in range up to MAX_DATES total
      for (const d of rangeDates) {
        if (next.size >= MAX_DATES) break;
        next.add(d);
      }
      return next;
    });
    // The double-clicked date becomes the new anchor
    setRangeAnchor(endStr);
  };

  const isAnyLoading = menuQueries.some((q) => q.isLoading);
  const orderedMeals = MEAL_ORDER.filter((m) => mealTypes.has(m));

  const loadedEntries = useMemo(
    () =>
      datesList.reduce<{ date: string; menuData: MenuDayForDateResponse }[]>(
        (acc, date, idx) => {
          const q = menuQueries[idx];
          if (q?.data) acc.push({ date, menuData: q.data });
          return acc;
        },
        [],
      ),
    [datesList, menuQueries],
  );

  // Which meals have at least one item across all loaded dates — serialized for stable deps
  const mealsWithDataKey = useMemo(
    () =>
      MEAL_ORDER.filter((m) => loadedEntries.some((e) => getMealItemCount(e.menuData, m) > 0)).join(','),
    [loadedEntries],
  );
  const mealsWithData = useMemo(() => new Set<MealType>(mealsWithDataKey ? mealsWithDataKey.split(',') as MealType[] : []), [mealsWithDataKey]);

  // Auto-switch to a meal with data if all currently selected meals have no data
  useEffect(() => {
    if (loadedEntries.length === 0 || mealsWithData.size === 0) return;
    const hasAnySelected = MEAL_ORDER.some((m) => mealTypes.has(m) && mealsWithData.has(m));
    if (!hasAnySelected) {
      const first = MEAL_ORDER.find((m) => mealsWithData.has(m));
      if (first) setMealTypes(new Set([first]));
    }
  }, [loadedEntries.length, mealsWithDataKey, mealTypes]);

  // Build all (date, meal) combos that have items, in canonical order
  const printCombos = loadedEntries.flatMap(({ date, menuData }) =>
    orderedMeals
      .filter((meal) => getMealItemCount(menuData, meal) > 0)
      .map((meal) => ({ date, meal, menuData })),
  );

  const errorDates = datesList.filter((_, idx) => menuQueries[idx]?.isError);
  const nonEmptyCombos = printCombos.length;
  const canExport = nonEmptyCombos > 0 && orderedMeals.length > 0 && !isAnyLoading;

  // Keep previewIndex in bounds when combos change
  const safePreviewIndex = nonEmptyCombos > 0 ? Math.min(previewIndex, nonEmptyCombos - 1) : 0;
  const previewCombo = printCombos[safePreviewIndex] ?? null;

  // Scale preview to fit ~420px container width (template is 816px = 8.5in @ 96dpi)
  const PREVIEW_SCALE = 0.514;
  const PREVIEW_HEIGHT = Math.round(1056 * PREVIEW_SCALE); // ~543px

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disablePortal={false}
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={600}>Export Menu</Typography>
          <ToggleButtonGroup
            value={menuMode}
            exclusive
            onChange={(_e, val) => { if (val) setMenuMode(val); }}
            size="small"
          >
            <ToggleButton value="daily">Daily Menus</ToggleButton>
            <ToggleButton value="alacarte">À La Carte</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {menuMode === 'daily' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Click a start date, then double-click an end date to select a range. Up to {MAX_DATES} days at a time.
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', p: 0, overflow: 'hidden', flex: 1 }}>

        {menuMode === 'alacarte' ? (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <AlaCarteMenuBuilder
              siteId={siteId}
              communityName={communityName}
              bugImageUrl={bustedBugImageUrl}
            />
          </Box>
        ) : (
        <>
        {/* ── Left: controls ── */}
        <Box sx={{ width: 360, flexShrink: 0, overflowY: 'auto', p: 3, borderRight: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={3}>
            {/* Multi-select calendar */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Dates
                {selectedDates.size > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({selectedDates.size}{maxReached ? '/7 max' : ''})
                  </Typography>
                )}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <DateCalendar
                  value={null}
                  onChange={() => {/* handled by MultiSelectDay click handlers */}}
                  slots={{ day: MultiSelectDay }}
                  slotProps={{ day: { selectedDates, maxReached, rangeAnchor, onDoubleClickDate: handleDoubleClickDate, onSingleClickDate: handleSingleClickDate } as any }}
                  sx={{ m: 0 }}
                />
              </Box>
              {maxReached && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                  Max 7 days. Remove a date to select another.
                </Typography>
              )}
              {datesList.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                  {datesList.map((date) => (
                    <Chip
                      key={date}
                      label={formatChipDate(date)}
                      size="small"
                      onDelete={() => setSelectedDates((prev) => {
                        const next = new Set(prev);
                        next.delete(date);
                        return next;
                      })}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Divider />

            {/* Meal types */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Meals</Typography>
              <Stack>
                {MEAL_ORDER.map((meal) => {
                  const hasData = loadedEntries.length === 0 || mealsWithData.has(meal);
                  return (
                    <FormControlLabel
                      key={meal}
                      control={
                        <Checkbox
                          checked={mealTypes.has(meal)}
                          onChange={() => {
                            setMealTypes((prev) => {
                              const next = new Set(prev);
                              if (next.has(meal)) {
                                if (next.size > 1) next.delete(meal);
                              } else {
                                next.add(meal);
                              }
                              return next;
                            });
                          }}
                          size="small"
                          disabled={loadedEntries.length > 0 && !hasData}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {MEAL_LABEL[meal]}
                          {loadedEntries.length > 0 && !hasData && ' (no data)'}
                        </Typography>
                      }
                    />
                  );
                })}
              </Stack>
            </Box>

            {/* Status */}
            {isAnyLoading && (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading menu data…</Typography>
              </Box>
            )}
            {errorDates.length > 0 && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                No data for: {errorDates.map(formatChipDate).join(', ')}
              </Alert>
            )}

            <Divider />

            {/* Options */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Options</Typography>
              <FormControlLabel
                control={<Switch checked={showDate} onChange={(e) => setShowDate(e.target.checked)} size="small" />}
                label={<Typography variant="body2">Show date on printed menu</Typography>}
              />
            </Box>
          </Stack>
        </Box>

        {/* ── Right: preview ── */}
        <Box sx={{ flex: 1, bgcolor: 'grey.100', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Preview toolbar */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            bgcolor: 'grey.200',
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
              {previewCombo
                ? `${MEAL_LABEL[previewCombo.meal]} — ${formatChipDate(previewCombo.date)}`
                : 'Select a date to preview'}
            </Typography>
            {nonEmptyCombos > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <IconButton size="small" onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))} disabled={safePreviewIndex === 0}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption">{safePreviewIndex + 1} / {nonEmptyCombos}</Typography>
                <IconButton size="small" onClick={() => setPreviewIndex((i) => Math.min(nonEmptyCombos - 1, i + 1))} disabled={safePreviewIndex === nonEmptyCombos - 1}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Scaled page */}
          <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', justifyContent: 'center', pt: 2, pb: 2 }}>
            {previewCombo ? (
              <Box sx={{ width: `${Math.round(816 * PREVIEW_SCALE)}px`, height: `${PREVIEW_HEIGHT}px`, flexShrink: 0, overflow: 'hidden', boxShadow: 3 }}>
                <Box sx={{ transform: `scale(${PREVIEW_SCALE})`, transformOrigin: 'top left', width: `${100 / PREVIEW_SCALE}%`, pointerEvents: 'none' }}>
                  <MenuPrintPage
                    mealType={previewCombo.meal}
                    menuData={previewCombo.menuData}
                    communityName={communityName}
                    date={previewCombo.date}
                    showDate={showDate}
                    bugImageUrl={bustedBugImageUrl}
                    textAlign="center"
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                <Typography variant="body2" color="text.disabled">No preview available</Typography>
              </Box>
            )}
          </Box>
        </Box>

        </>
        )}

      </DialogContent>

      {/* Hidden printable content (daily menus only) */}
      {menuMode === 'daily' && (
        <div style={{ position: 'fixed', left: '-10000px', top: 0 }}>
          <div ref={printRef}>
            {printCombos.map(({ date, meal, menuData }, idx) => (
              <div key={`${date}-${meal}`} style={idx < printCombos.length - 1 ? { pageBreakAfter: 'always' } : {}}>
                <MenuPrintPage
                  mealType={meal}
                  menuData={menuData}
                  communityName={communityName}
                  date={date}
                  showDate={showDate}
                  bugImageUrl={bustedBugImageUrl}
                  textAlign="center"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {menuMode === 'daily' && (
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handlePrint()}
            disabled={!canExport}
          >
            {isAnyLoading ? 'Loading\u2026' : 'Export Menu'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
